import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import StepProgress from './StepProgress';
import SuccessBanner from './SuccessBanner';
import { PAYMENT_PROVIDER, SUPPORT_EMAIL, WHATSAPP_NUMBER, withBase } from '../../publicSite/config';
import { trackEvent } from '../../publicSite/analytics';
import { createPayFastCheckout, redirectToPayFast, waitForPayFastPayment } from '../../utils/payfastHostedCheckout';

const sponsorTypes = [
  { key: 'student', label: 'Sponsor a Student' },
  { key: 'youth-group', label: 'Sponsor a Youth Group' },
  { key: 'resources', label: 'Fund Resources' },
  { key: 'corporate', label: 'Corporate/Church' }
];

const amountChips = [150, 300, 500, 1000];

export default function SponsorForm() {
  const location = useLocation();
  const [selectedType, setSelectedType] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [selectedAmount, setSelectedAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    sponsorTarget: 'Child',
    honorOf: '',
    message: '',
    updates: false,
    receipt: false,
    talkToUs: false
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    trackEvent('public_partnerships_select_type', { type: selectedType });
  }, [selectedType]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get('payment');
    const orderId = params.get('orderId') || '';
    if (payment === 'success') {
      if (!orderId) {
        setSubmitError('Missing sponsorship order reference.');
        return;
      }
      waitForPayFastPayment(orderId)
        .then(() => {
          setSuccess(true);
          setSubmitError('');
          localStorage.removeItem('shem_pending_partnership');
          trackEvent('public_partnerships_success');
        })
        .catch((error) => {
          setSubmitError(error?.message || 'Payment confirmation is still pending.');
        });
    } else if (payment === 'cancel') {
      setSubmitError('Payment was cancelled. You can try again.');
      localStorage.removeItem('shem_pending_partnership');
    }
  }, [location.search]);

  const amountValue = useMemo(() => {
    const parsed = Number(customAmount);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    return selectedAmount;
  }, [customAmount, selectedAmount]);

  const steps = [
    { label: 'Choose Type', state: selectedType ? 'is-done' : 'is-active' },
    { label: 'Choose Amount', state: amountValue > 0 ? 'is-done' : selectedType ? 'is-active' : 'is-pending' },
    { label: 'Your Details', state: form.name && form.email && form.phone ? 'is-done' : amountValue > 0 ? 'is-active' : 'is-pending' },
    { label: 'Confirm', state: success ? 'is-done' : form.name && form.email && form.phone && amountValue > 0 ? 'is-active' : 'is-pending' }
  ];

  const baseErrors = useMemo(() => {
    const nextErrors = {};
    if (!selectedType) nextErrors.selectedType = 'Select a sponsorship type.';
    if (!frequency) nextErrors.frequency = 'Select a frequency.';
    if (!amountValue || amountValue <= 0) nextErrors.amount = 'Enter a valid amount.';
    if (!form.name.trim()) nextErrors.name = 'Name is required.';
    if (!/\S+@\S+\.\S+/.test(form.email)) nextErrors.email = 'Enter a valid email.';
    if (!form.phone.trim()) nextErrors.phone = 'Phone is required.';
    return nextErrors;
  }, [selectedType, frequency, amountValue, form.name, form.email, form.phone]);

  const isValid = Object.keys(baseErrors).length === 0;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors(baseErrors);
    setSubmitError('');
    setSuccess(false);
    if (Object.keys(baseErrors).length > 0) return;
    setSubmitting(true);
    const payload = {
      sponsorType: selectedType,
      frequency,
      amount: amountValue,
      donorDetails: {
        name: form.name,
        email: form.email,
        phone: form.phone,
        organization: form.organization
      },
      sponsorshipDetails: {
        target: form.sponsorTarget,
        honorOf: form.honorOf,
        message: form.message
      },
      preferences: {
        updates: form.updates,
        receipt: form.receipt,
        talkToUs: form.talkToUs
      },
      paymentProvider: PAYMENT_PROVIDER
    };

    try {
      const orderId = `sponsorship-${Date.now()}`;
      localStorage.setItem(
        'shem_pending_partnership',
        JSON.stringify({
          ...payload,
          orderId,
          submittedAt: new Date().toISOString()
        })
      );

      const [firstName = '', ...restName] = String(form.name || '').trim().split(/\s+/);
      const checkout = await createPayFastCheckout({
        amount: amountValue,
        itemName: 'School of Shem Sponsorship',
        itemDescription: `${sponsorTypes.find((type) => type.key === selectedType)?.label || 'Sponsorship'} • ${frequency === 'monthly' ? 'Monthly' : 'Once-off'}`,
        successPath: `${withBase('/partnerships')}?payment=success&orderId=${encodeURIComponent(orderId)}`,
        cancelPath: `${withBase('/partnerships')}?payment=cancel&orderId=${encodeURIComponent(orderId)}`,
        metadata: {
          orderId,
          type: 'partnership_sponsorship',
          source: 'partnerships_page',
          sponsorType: selectedType,
          frequency,
          amount: amountValue,
          donorDetails: payload.donorDetails,
          sponsorshipDetails: payload.sponsorshipDetails,
          preferences: payload.preferences
        },
        customer: {
          firstName,
          lastName: restName.join(' '),
          email: form.email,
          phone: form.phone
        }
      });

      trackEvent('public_partnerships_submit', { frequency, amount: amountValue });
      redirectToPayFast(checkout);
      return;
    } catch (error) {
      setSubmitError(error?.message || 'Unable to start Payfast checkout.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      // no-op
    }
  };

  return (
    <div className="ps-form-layout">
      <div className="ps-form-summary">
        <div className="ps-card ps-summary-card">
          <h3>Summary</h3>
          <p><strong>Type:</strong> {selectedType ? sponsorTypes.find(t => t.key === selectedType)?.label : 'Select a type'}</p>
          <p><strong>Frequency:</strong> {frequency === 'monthly' ? 'Monthly' : 'Once-off'}</p>
          <p><strong>Amount:</strong> {amountValue > 0 ? `R${amountValue}` : 'Select amount'}</p>
          <p><strong>Estimated impact:</strong> Supports ongoing formation and resources.</p>
          <div style={{ marginTop: '12px', fontSize: '0.85rem', color: 'var(--shem-muted)' }}>
            Next steps: We confirm your sponsorship and send instructions within 24–48 hours.
          </div>
          <div style={{ marginTop: '12px', fontSize: '0.85rem', color: 'var(--shem-muted)' }}>
            Privacy: We protect your details and only use them for sponsorship communication.
          </div>
        </div>
      </div>
      <div className="ps-form-panel">
        <StepProgress steps={steps} />
        {success && (
          <SuccessBanner
            title="Payment successful"
            message="Your sponsorship payment has been received successfully. We will follow up with confirmation and next steps."
          />
        )}
        <form className="ps-form" onSubmit={handleSubmit}>
          <div>
            <label className="ps-label">Sponsor Type</label>
            <div className="ps-radio-grid">
              {sponsorTypes.map((type) => (
                <button
                  key={type.key}
                  type="button"
                  className={`ps-radio-card ${selectedType === type.key ? 'is-selected' : ''}`}
                  onClick={() => setSelectedType(type.key)}
                >
                  {type.label}
                </button>
              ))}
            </div>
            {errors.selectedType && <div className="ps-error">{errors.selectedType}</div>}
          </div>

          <div>
            <label className="ps-label">Frequency</label>
            <div className="ps-toggle">
              <button type="button" className={frequency === 'monthly' ? 'is-active' : ''} onClick={() => setFrequency('monthly')}>Monthly</button>
              <button type="button" className={frequency === 'once' ? 'is-active' : ''} onClick={() => setFrequency('once')}>Once-off</button>
            </div>
          </div>

          <div>
            <label className="ps-label">Amount (ZAR)</label>
            <div className="ps-chip-row">
              {amountChips.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className={`ps-chip-button ${selectedAmount === amount ? 'is-selected' : ''}`}
                  onClick={() => {
                    setSelectedAmount(amount);
                    setCustomAmount('');
                    trackEvent('public_partnerships_select_amount', { amount });
                  }}
                >
                  R{amount}
                </button>
              ))}
            </div>
            <input
              className="ps-input"
              type="number"
              min="1"
              placeholder="Custom amount"
              value={customAmount}
              onChange={(e) => {
                setCustomAmount(e.target.value);
                setSelectedAmount(0);
              }}
            />
            {errors.amount && <div className="ps-error">{errors.amount}</div>}
          </div>

          <div>
            <label className="ps-label">Donor Details</label>
            <div className="ps-form-grid">
              <input className="ps-input" placeholder="Full name" value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} />
              <input className="ps-input" placeholder="Email" value={form.email} onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))} />
              <input className="ps-input" placeholder="Phone / WhatsApp" value={form.phone} onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))} />
              <input className="ps-input" placeholder="Organization (optional)" value={form.organization} onChange={(e) => setForm(prev => ({ ...prev, organization: e.target.value }))} />
            </div>
            {errors.name && <div className="ps-error">{errors.name}</div>}
            {errors.email && <div className="ps-error">{errors.email}</div>}
            {errors.phone && <div className="ps-error">{errors.phone}</div>}
          </div>

          <div>
            <label className="ps-label">Sponsorship Details</label>
            <div className="ps-form-grid">
              <select
                className="ps-input"
                value={form.sponsorTarget}
                onChange={(e) => setForm(prev => ({ ...prev, sponsorTarget: e.target.value }))}
              >
                <option value="Child">Child</option>
                <option value="Youth">Youth</option>
                <option value="Group">Group</option>
                <option value="Program">Program</option>
              </select>
              <input
                className="ps-input"
                placeholder="In honor of (optional)"
                value={form.honorOf}
                onChange={(e) => setForm(prev => ({ ...prev, honorOf: e.target.value }))}
              />
            </div>
            <textarea
              className="ps-input"
              rows="3"
              placeholder="Message or notes"
              value={form.message}
              onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
            />
          </div>

          <div>
            <label className="ps-label">Preferences</label>
            <div className="ps-checkboxes">
              <label><input type="checkbox" checked={form.updates} onChange={(e) => setForm(prev => ({ ...prev, updates: e.target.checked }))} />Send me updates</label>
              <label><input type="checkbox" checked={form.receipt} onChange={(e) => setForm(prev => ({ ...prev, receipt: e.target.checked }))} />I’d like an invoice/receipt</label>
              <label><input type="checkbox" checked={form.talkToUs} onChange={(e) => setForm(prev => ({ ...prev, talkToUs: e.target.checked }))} />I’d like to speak to someone</label>
            </div>
          </div>

          {submitError && <div className="ps-error">{submitError}</div>}

          <button className="btn ps-btn-primary btn-lg" type="submit" disabled={submitting || !isValid}>
            {submitting ? 'Redirecting to Payfast...' : frequency === 'monthly' ? 'Become a Monthly Partner' : 'Sponsor Now'}
          </button>
          {success && (
            <div style={{ marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button type="button" className="btn ps-btn-outline" onClick={handleCopyLink}>Share this mission</button>
              <a className="btn ps-btn-outline" href={`mailto:${SUPPORT_EMAIL}`}>Email us</a>
              {WHATSAPP_NUMBER === 'WHATSAPP_NUMBER' ? (
                <button className="btn ps-btn-outline" type="button" disabled>WhatsApp</button>
              ) : (
                <a className="btn ps-btn-outline" href={`https://wa.me/${WHATSAPP_NUMBER}`}>WhatsApp</a>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
